---
title: "Find prospects: smart search and Missions"
description: "Two ways to find candidates in Chosen — internal smart search over your existing pool, and Missions that run a natural-language query against the web."
category: "sourcing"
keywords:
  - "find prospects"
  - "sourcing search"
  - "candidate web search"
  - "passive candidate search"
  - "sourcing missions"
schema: "HowTo"
nav:
  section: "guides"
  order: 10
  parent: "/guides/sourcing"
---

The Sourcing tab finds people two ways. The **Prospects** tab runs a smart search over people you've already sourced — free, instant, no external lookup. The **Search** tab runs a **Mission**: you describe the role in plain language and Chosen searches the web for matches. Use the first when the right person might already be in your pool; use the second when they aren't.

## Search your existing pool

Open **Sourcing → Prospects**. The search box at the top reads your query in plain language — "senior backend engineers in Berlin who've worked at a fintech" — and ranks the prospects you've already sourced against it. No web lookup, no usage cost. It's the fast way to notice that someone a Mission surfaced three months ago is suddenly right for a new role.

<Steps>
  <li>Go to **Sourcing → Prospects**.</li>
  <li>Type what you're looking for in the search box. A sentence works better than keywords.</li>
  <li>Results re-rank in place. Click any prospect to open the full record.</li>
</Steps>

This is the same idea as [smart search for candidates](/guides/search/smart-vs-deep), scoped to your sourcing pool instead of your applicant pool.

## Run a Mission against the web

A Mission searches the web for people who don't yet exist in your system.

<Steps>
  <li>Open **Sourcing → Search**.</li>
  <li>In the box, describe who you want — role, seniority, location, the kind of company they're at. Constraints in the sentence are read as constraints.</li>
  <li>Run the search. Results stream in live as Chosen finds them — you don't wait for the whole run to finish before you can read.</li>
  <li>If a job is selected in the left sidebar, the Mission is linked to that role, which helps later when you import.</li>
</Steps>

A Mission's results don't replace your earlier ones — they're added to the **Prospects** table, so the pool grows over time.

## What a prospect record shows

Each prospect Chosen surfaces comes with:

- **Name, current title, and current company** — the basics, pulled from public profiles.
- **A why-they-matched summary** — a short note on what in their background fits your query.
- **An assembled work history** — every public role Chosen could piece together, shown in full on the [experience timeline](/guides/sourcing/experience-timeline).
- **A best-guess work email, or a "no email available" flag** — confirming an address is a separate step. See [email enrichment](/guides/sourcing/enrichment).

Open a prospect and the detail drawer also shows skills, education, and a **Key insights** panel of auto-detected signals — career patterns worth knowing before you reach out.

<Callout type="info" title="A surfaced prospect is a lead, not a verified profile">
Web-sourced data is assembled from public pages. Titles can be stale, a work history can have gaps, and the matched summary is Chosen's read of the fit — not a guarantee. Skim the record before you spend time on a draft.
</Callout>

## Plan limits

Missions and enrichment draw on usage tiers — each run costs real money behind the scenes — so a heavy day of either can pause until the window resets.

The takeaway: don't re-run a Mission ten times to nudge the wording. Read the results, refine the description, then run again. Exact numbers live on [usage limits](/reference/usage-limits).

## When it gets it wrong

A Mission is a web search, so it inherits a web search's failure modes. A role described too loosely returns a loose, noisy list. A role described too narrowly returns three people. Niche or stealth-company candidates with thin public footprints may not surface at all — the absence of a prospect isn't proof they don't exist.

Treat the first run as a draft. The fix for a bad result is almost always a sharper description, not a different tool. Once you have prospects worth contacting, move on to [enrichment](/guides/sourcing/enrichment).
